/**
 * The ⌘K palette.
 *
 * Three entry points reach this one component — the sidebar's search trigger, the Help
 * popover's "Search help" row, and the global ⌘K binding — through useSearchStore, so
 * there is a single query and a single result list however you got here.
 *
 * On not using the shared `Modal` from @repo/ui: that component takes a required `title`,
 * renders a header row with a close button, and is fixed at 533px. A palette's header IS
 * the input, and it wants to be wider. Rather than fight those with overrides, this
 * builds on the same primitives the shared Modal builds on (MUI `Modal` + `Fade`) and
 * reuses its chrome tokens verbatim — `background.paper`, `borderRadius: 2`, a
 * `divider` border, `shadows[24]` — so the two read as the same surface. Every control
 * INSIDE it is a shared component.
 *
 * Note on Tooltip: `@repo/ui`'s Tooltip is a zero-byte file. It typechecks and is
 * exported from the barrel, so importing it fails only at runtime. Anything needing a
 * tooltip here must take it from `@mui/material`.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { ArrowRightIcon, BookOpenIcon, CompassIcon, CornerDownLeftIcon, FileTextIcon, HashIcon, MapPinIcon, SearchIcon } from 'lucide-react'

import { Box, Fade, Modal, Stack, Typography, useTheme } from '@mui/material'
import { ROUTES } from 'routes'

import { Input, Button } from '@repo/ui'
import { AskVeraIcon } from 'components/display/AskVeraIcon/AskVeraIcon'
import {
  TOURS,
  getOnPageTours,
  openToursDialog,
  useProductTour,
  type TourDef,
} from 'components/onboarding'
import { useCanAccess, useInternalRouteAccess, useReferrerState } from 'hooks'
import { brandGlossaryFor, glossaryHref } from 'docs/glossary'
import { useCustomerDocs } from 'docs/useCustomerDocs'
import { CUSTOMER_DOCS_LABEL, INTERNAL_DOCS_LABEL, isDocsPath } from 'docs/surface'
// Gated + dynamic. Returns an empty list for a brand user, and for a staffer loads the
// internal manifest from its own chunk — so internal guide titles and excerpts never enter
// the bundle this eagerly-routed component ships to customers (§43). See the hook's header.
import { useInternalDocs } from 'docs/useInternalDocs'

import { useAskStore } from 'ask'
import {
  SEARCH_COMMANDS,
  isDefinitionalQuery,
  normalizeQuery,
  searchCommands,
  searchDocEntries,
  searchGlossaryEntries,
  useSearchStore,
  type DocMatch,
  type GatedCommand,
  // Was USED in the Row union and never imported — `GlossaryMatch` resolved to an
  // implicit any, so the glossary row's shape was unchecked. Invisible until the
  // platform typecheck started actually running (it had been resolving a stale
  // TypeScript 4.9.5 that bailed before semantic analysis).
  type GlossaryMatch,
} from 'search'

/**
 * One selectable line.
 *
 * The list is flat for keyboard purposes even though it renders in groups — arrow keys
 * move through every row in order, and group headers are painted between rows rather than
 * being rows themselves. Making headers focusable is the classic palette bug: ↓ appears
 * to skip, because a "selected" header looks like nothing happened.
 */
type Row =
  | { kind: 'ask'; id: string; query: string }
  | { kind: 'command'; id: string; command: GatedCommand }
  /**
   * `internal` says WHICH docs site this result belongs to. It is not cosmetic: it picks the
   * route the row navigates to (/internal/docs vs /docs), the group it is filed under, and the
   * badge it carries. A staff search returns both corpora, and a row that does not say which
   * one it came from is how an internal runbook gets pasted into a customer conversation.
   */
  | { kind: 'doc'; id: string; match: DocMatch; internal: boolean }
  | { kind: 'glossary'; id: string; match: GlossaryMatch }
  | { kind: 'tour'; id: string; tour: TourDef; progressLabel?: string }
  | { kind: 'tours-all'; id: string }

/**
 * The tours group is labelled by WHAT IT CONTAINS, which differs by state.
 *
 * With nothing typed the rows are the tours whose anchors are on the page behind the
 * palette, so "Tours on this page" is the accurate heading and the more useful one —
 * it explains why these particular tours are being offered, which a bare "Tours" does
 * not. Once a query is typed the group holds matches from the WHOLE registry, and the
 * same heading would be a lie: those tours are mostly not on this page.
 *
 * Cheap to get wrong in the other direction too. Labelling both states "Tours on this
 * page" would quietly teach people that search is page-scoped, and they would stop
 * using it to find anything else.
 */
const TOURS_GROUP_ON_PAGE = 'Tours on this page'
const TOURS_GROUP_GLOBAL = 'Tours'

const rowGroup = (row: Row, toursAreOnPage: boolean): string => {
  if (row.kind === 'ask') return 'Ask Vera'
  if (row.kind === 'command') return row.command.group
  if (row.kind === 'glossary') return 'Glossary'
  if (row.kind === 'tour' || row.kind === 'tours-all') {
    return toursAreOnPage ? TOURS_GROUP_ON_PAGE : TOURS_GROUP_GLOBAL
  }
  // Two docs sites, two groups (§43). The labels come from `docs/surface`, which is the one
  // module both the sources and this component may read them from.
  return row.internal ? INTERNAL_DOCS_LABEL : CUSTOMER_DOCS_LABEL
}

/**
 * How many tour rows the palette shows. Same reasoning as the Help menu's cap: the
 * palette is a router, and "Browse all tours" is one row away.
 */
const TOUR_LIMIT = 3

/**
 * How many glossary rows to show. Small: these rows are TALLER than the others (they carry a
 * definition, not just a label), so four already occupies the space eight command rows would, and
 * an exact term match is usually one entry rather than a list.
 */
const GLOSSARY_LIMIT = 4

/**
 * The footer actions' type size.
 *
 * `@repo/ui` Button hard-codes `fontSize: 16` for EVERY size — `small` is a 32px-tall
 * control carrying 16px text, and `x-small` a 28px one carrying the same. That is why
 * these read as oversized next to the keyboard hints they sit beside, which are
 * `body4`. 13px puts them in the same register as the rest of the footer.
 *
 * Overridden here rather than fixed in the Button: making font size track size is
 * almost certainly right, but it would restyle every small/x-small button in the
 * platform, and that is a reviewed visual pass of its own — not a side effect of
 * tuning one footer. Noted in the report so it can be picked up deliberately.
 */
const FOOTER_BUTTON_SX = { textTransform: 'none', fontSize: 13 } as const

/** How many docs rows the palette shows before it stops being scannable. */
const DOC_LIMIT = 8
const COMMAND_LIMIT = 8
/** With no query typed there is nothing to rank, so show a fixed starting set. */
const EMPTY_STATE_COMMAND_IDS = ['analytics', 'certificates', 'products', 'people', 'docs', 'settings']

export const SearchOverlay = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  // Where the user was when the palette opened — the palette does not change the route,
  // so this is still their page. Handed to docs results as back-navigation state (§42).
  //
  // Suppressed when that page is itself a docs page, for the reason the Help popover gives:
  // within docs the left nav already says where you are, so a back link to the guide you just
  // left is noise — and the label would be derived by title-casing that guide's SLUG, which is
  // how "← Back to Sms Twilio Gcp Setup" appeared. `isDocsPath` covers both docs sites.
  const location = useLocation()
  const referrerState = useReferrerState()
  const referrer = isDocsPath(location.pathname) ? undefined : referrerState

  const open = useSearchStore(s => s.open)
  const query = useSearchStore(s => s.query)
  const setQuery = useSearchStore(s => s.setQuery)
  const closeSearch = useSearchStore(s => s.closeSearch)
  const ask = useAskStore(s => s.ask)
  // For the pinned row's no-query state (VRG-227) — opens the panel without queuing a
  // blind send. `ask('')` would have been harmless too (`useAskRunner` only sends a
  // truthy `pendingQuestion`), but this says what it means at the call site instead of
  // relying on that downstream falsy-check as the reason it's safe.
  const openAskPanel = useAskStore(s => s.openPanel)
  const { startTour, tourProgress } = useProductTour()

  /**
   * The staff corpus — empty for a brand user, so the second `searchDocEntries` call below is
   * a no-op for them and no internal title can reach their result list.
   *
   * The requirement in one line: a brand user searches brand docs; staff search both. It is the
   * palette that has to hold it, because the deleted docs card grid (VRG-402) took with it the
   * only surface that ever searched the internal corpus, leaving the search field rendered in
   * the internal docs nav column quietly querying the customer one.
   */
  const internalDocs = useInternalDocs()
  // VRG-680 — the customer corpus and brand glossary as gated by the launch switches, so a
  // gated guide or term (Refer & earn while its switch is off) never appears in results.
  const { source: customerDocs, switches } = useCustomerDocs()
  const brandGlossary = brandGlossaryFor(switches)

  /**
   * Tours relevant to the page underneath, captured when the palette OPENS.
   *
   * Must be a snapshot rather than a live read: `getOnPageTours` inspects the DOM
   * for `data-tour` anchors, and the palette is a modal — once it is open, the page
   * it is meant to describe is behind a backdrop, and re-reading during render would
   * also make rendering a side effect.
   */
  const [onPageTours, setOnPageTours] = useState<TourDef[]>([])
  useEffect(() => {
    if (open) setOnPageTours(getOnPageTours())
  }, [open])

  // Gates are resolved once, here, because useCanAccess is a hook and cannot be called
  // per row. GatedPermission in commands.ts is the union these two cover — adding a gate
  // without adding its check will not compile.
  //
  // VRG-410 — `gate.internal` now asks the route→permission table by PATH rather than a
  // flat `useCanAccess({ role: 'platform_admin' })`. That boolean was a THIRD answer to
  // "which internal pages can this person open", after the sidebar's and the route
  // guard's, and it disagreed with both: it hid every internal row from platform_manager
  // (who can open most of them) while showing Subscriptions, Invoices, Users and Labs to
  // an admin as if they were ungated. Asking by path also makes the palette correct for
  // `vera_fulfillment` / `vera_finance` for free, rather than handing them rows that
  // redirect on arrival.
  const { canReachInternalPath } = useInternalRouteAccess()
  const { isHasAccess: canViewOrders } = useCanAccess({ action: 'internal_order_view' })
  const { isHasAccess: canViewInventory } = useCanAccess({ action: 'tags_inventory_view' })

  const [active, setActive] = useState(0)
  const listRef = useRef<HTMLDivElement | null>(null)

  const allowed = useMemo(() => {
    const perms: Record<string, boolean> = {
      tags_inventory_view: canViewInventory,
      internal_order_view: canViewOrders,
    }
    return SEARCH_COMMANDS.filter(c => {
      if (c.gate?.internal && !canReachInternalPath(c.path)) return false
      // Fail closed: a gate whose permission is not in the map above hides the row.
      if (c.gate?.permission) return perms[c.gate.permission] === true
      return true
    })
  }, [canReachInternalPath, canViewOrders, canViewInventory])

  const rows: Row[] = useMemo(() => {
    const trimmed = query.trim()

    /*
      Tours for THIS page, plus a way into the library.

      Placed first among the NAVIGABLE rows in the empty state — above Pages, below the
      pinned Ask row (VRG-227) — and that ordering is the claim worth defending: someone
      who opens ⌘K without typing has not yet said what they want, and the most useful
      thing to offer a person still orienting is the guided walkthrough of the screen
      they are already looking at. Once they type, intent exists and navigation
      outranks instruction — so below, tours sit under commands.

      `onPageTours` is snapshotted when the palette opens (see the effect below); it
      reads the live DOM, which is only meaningful while the underlying page is the
      one being described.
    */
    const tourRows = (tours: TourDef[]): Row[] => [
      ...tours.slice(0, TOUR_LIMIT).map(tour => {
        const p = tourProgress(tour.id)
        const step = p?.currentStep ?? 0
        return {
          kind: 'tour' as const,
          id: `tour:${tour.id}`,
          tour,
          progressLabel:
            p?.status === 'completed'
              ? 'Completed'
              : step > 0
                ? `${Math.min(step + 1, tour.steps.length)} of ${tour.steps.length}`
                : undefined,
        }
      }),
      { kind: 'tours-all' as const, id: 'tours-all' },
    ]

    if (!trimmed) {
      const commands = allowed
        .filter(c => EMPTY_STATE_COMMAND_IDS.includes(c.id))
        .sort(
          (a, b) =>
            EMPTY_STATE_COMMAND_IDS.indexOf(a.id) - EMPTY_STATE_COMMAND_IDS.indexOf(b.id),
        )
        .map(command => ({ kind: 'command' as const, id: `cmd:${command.id}`, command }))
      // Ask is pinned FIRST here too (VRG-227), same reasoning as the typed-query path
      // below — it always has an answer, and it is the escape hatch for someone who
      // opened ⌘K with no query in mind at all, not just someone whose search came up
      // short. `query: ''` — `run()` reads an empty query as "open the panel", never as
      // a question to send (see its 'ask' branch).
      const askRow: Row = { kind: 'ask' as const, id: 'ask', query: '' }
      // Only when this page HAS a tour. Emitting the group unconditionally put a
      // "Tours / Browse all tours" header in the palette's top slot on every
      // tour-less page — which is most of them today — and that directly
      // contradicts the reason the group sits first at all. The top slot is
      // earned by being about the page in front of you; a bare link to the
      // library is not, and the footer's "Product tours" button already offers
      // that from everywhere.
      return onPageTours.length > 0
        ? [askRow, ...tourRows(onPageTours), ...commands]
        : [askRow, ...commands]
    }

    const commands: Row[] = searchCommands(allowed, trimmed, COMMAND_LIMIT).map(m => ({
      kind: 'command' as const,
      id: `cmd:${m.command.id}`,
      command: m.command as GatedCommand,
    }))

    /*
      Docs results, over ONE corpus for a brand user and TWO for a staffer.

      `internalDocs` is empty unless the user is Vera staff, so the second call collapses to
      nothing for everyone else and the customer path is unchanged. Ranking is done ACROSS the
      corpora — the top DOC_LIMIT by score, whichever site they came from — rather than by
      giving each site its own quota. A fixed quota would evict a strong customer match to make
      room for a weak internal one on every query where the answer only lives in one of them,
      which for a staffer is most queries.

      Display then groups the survivors, so each heading is painted once instead of the two
      alternating down the list as scores interleave. Which block leads is decided by the better
      match rather than by a standing preference for one site: a staffer searching "encode a
      tag" wants the runbook first, and one searching "what is a certificate" wants the guide,
      and nothing but the score knows which of those was typed.
    */
    const ranked = [
      ...searchDocEntries(customerDocs.docs, trimmed, DOC_LIMIT).map(match => ({ match, internal: false })),
      ...searchDocEntries(internalDocs, trimmed, DOC_LIMIT).map(match => ({ match, internal: true })),
    ]
      .sort((a, b) => b.match.score - a.match.score)
      .slice(0, DOC_LIMIT)

    // `ranked` is score-ordered, so the first row of each kind IS that kind's best match.
    const bestInternal = ranked.findIndex(r => r.internal)
    const bestCustomer = ranked.findIndex(r => !r.internal)
    const internalLeads = bestInternal !== -1 && (bestCustomer === -1 || bestInternal < bestCustomer)

    const docs: Row[] = [internalLeads, !internalLeads].flatMap(internal =>
      ranked
        .filter(r => r.internal === internal)
        .map(({ match }) => ({
          kind: 'doc' as const,
          // The surface is part of the key. Nothing stops the two sites from publishing the
          // same category/slug, and two rows sharing a React key silently render as one.
          id: `doc:${internal ? 'internal' : 'customer'}:${match.doc.category}/${match.doc.slug}${
            match.heading ? `#${match.heading.id}` : ''
          }`,
          match,
          internal,
        })),
    )

    /*
      Glossary ABOVE docs, and that ordering is the point rather than a preference: typing a term of
      art is a request for its MEANING, and an entry answers it exactly, in one sentence, right in
      the row — whereas a guide match only promises the word appears somewhere on a page you have
      still to open and scan. Below the commands, though: "certificates" is more likely to mean
      "take me to Certificates" than "define certificate".
    */
    const glossary: Row[] = searchGlossaryEntries(brandGlossary, trimmed, GLOSSARY_LIMIT).map(match => ({
      kind: 'glossary' as const,
      id: `gloss:${match.entry.id}`,
      match,
    }))

    // Ask is pinned FIRST, not appended as a fallback. It is the row that always has an
    // answer, and burying it under a short list of weak lexical matches would make the
    // most useful option the least visible exactly when the search did badly.
    // Searching is global by intent, so match the whole registry rather than only
    // what happens to be on this page — the on-page bias belongs to the untyped
    // state, where there is nothing else to rank by.
    // The NORMALISED text, for the same reason the scorer uses it: this is a raw substring
    // filter, so it is the one group in the palette that would otherwise still be matched on
    // the words the question was wrapped in. Left as typed, `certificate` found the
    // Certificates tour and `what is a certificate` found nothing — the two are the same
    // search everywhere else in this list, and a group that disagrees is just a hole.
    const q = normalizeQuery(trimmed).text
    const tours = tourRows(
      TOURS.filter(
        t =>
          t.label.toLowerCase().includes(q) ||
          t.description.toLowerCase().includes(q) ||
          t.category.toLowerCase().includes(q),
      ),
    )

    /*
      A DEFINITIONAL query moves the glossary above the pages, and nothing else does.

      The default order below is right for a bare noun and this does not disturb it:
      someone typing "tag" may well be navigating, so Tags / Tag shop / Tag orders
      deserve the top slots and the definition sits under them. But "what is a tag"
      cannot be a navigation request — nobody phrases "take me to Tags" as a question —
      so the ranking that serves the ambiguous case is simply wrong for the unambiguous
      one.

      Scoped to the definition-seeking openings only ("what is", "define", "meaning
      of"), NOT to questions generally: "how do I encode a tag" is procedural, and
      answering it with a dictionary entry above the page that does the thing would be
      the same mistake in the other direction. `isDefinitionalQuery` carries that split.
    */
    const definitional = isDefinitionalQuery(trimmed)

    return [
      { kind: 'ask' as const, id: 'ask', query: trimmed },
      ...(definitional ? glossary : []),
      ...commands,
      // Above glossary and docs, below commands: a tour DOES something, where a
      // definition or a guide describes it. Only rendered when a tour actually
      // matched — `tourRows` is called with the filtered list, and the trailing
      // "Browse all tours" row is dropped with it when that list is empty.
      ...(tours.length > 1 ? tours : []),
      ...(definitional ? [] : glossary),
      ...docs,
    ]
  }, [query, allowed, internalDocs, onPageTours, tourProgress, customerDocs, brandGlossary])

  // A new query invalidates the old selection index — without this, typing while row 5 is
  // selected leaves the highlight on whatever unrelated row now occupies slot 5.
  useEffect(() => setActive(0), [query])
  useEffect(() => {
    if (open) setActive(0)
  }, [open])

  useEffect(() => {
    if (active >= rows.length) setActive(Math.max(0, rows.length - 1))
  }, [rows.length, active])

  // Keep the highlighted row on screen when it moves by keyboard.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-row-index="${active}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [active])

  const run = useCallback(
    (row: Row) => {
      if (row.kind === 'ask') {
        // The handoff: the panel opens carrying what was typed here, so the question does
        // not have to be retyped. Closing the palette is what makes the page usable
        // again — the panel deliberately does not block it.
        //
        // Empty query (the pinned no-typing row, VRG-227) just OPENS the panel — the user
        // hasn't committed to a question yet, so there is nothing to send. A query typed
        // and deliberately activated has already been committed to, so it sends.
        if (row.query) {
          ask(row.query)
        } else {
          openAskPanel()
        }
        closeSearch()
        return
      }
      if (row.kind === 'command') {
        navigate(row.command.path)
        closeSearch()
        return
      }
      if (row.kind === 'tour') {
        // Close BEFORE starting: the tour spotlights anchors on the page underneath
        // and this modal's backdrop would cover every one of them.
        closeSearch()
        startTour(row.tour.id)
        return
      }
      if (row.kind === 'tours-all') {
        closeSearch()
        openToursDialog()
        return
      }
      if (row.kind === 'glossary') {
        // The term's anchor on the glossary page, not a search for the word (§44). The referrer
        // rides along for the same reason a doc result carries it.
        navigate(glossaryHref(row.match.entry.id), { state: referrer })
        closeSearch()
        return
      }
      const { doc, heading } = row.match
      // ROUTES.DOC / ROUTES.INTERNAL_DOC, not a hand-written path: an anchor result is still
      // the docs route, and the fragment is the only part that is this feature's business.
      // The two sites are two routes, so the row's surface picks the builder — an internal
      // result opened at /docs/... would 404 on a slug the customer artifact has never heard of.
      //
      // The referrer rides along so the guide can offer a way back to whatever the user was
      // doing when they hit ⌘K (§42). Searching the docs mid-task is the same interrupted
      // navigation as the Help popover's Documentation row.
      const docPath = row.internal
        ? ROUTES.INTERNAL_DOC(doc.category, doc.slug)
        : ROUTES.DOC(doc.category, doc.slug)
      navigate(`${docPath}${heading ? `#${heading.id}` : ''}`, {
        state: referrer,
      })
      closeSearch()
    },
    [ask, openAskPanel, closeSearch, navigate, referrer],
  )

  /**
   * All keyboard handling hangs off the wrapper, not the field.
   *
   * `@repo/ui`'s Input accepts a `handleKeyDown` prop, destructures it, and then never
   * passes it to the underlying element — its own internal `onKeyDown` wins. Rather than
   * change a shared component every form in the app renders, this listens on the
   * container and lets the event bubble up from the input. `preventDefault` still works
   * from here: the default action runs after dispatch completes, not during it.
   */
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      closeSearch()
      return
    }
    if (rows.length === 0) return

    if (e.key === 'ArrowDown' || (e.key === 'n' && e.ctrlKey)) {
      e.preventDefault()
      setActive(i => (i + 1) % rows.length)
      return
    }
    if (e.key === 'ArrowUp' || (e.key === 'p' && e.ctrlKey)) {
      e.preventDefault()
      setActive(i => (i - 1 + rows.length) % rows.length)
      return
    }
    if (e.key === 'Home') {
      e.preventDefault()
      setActive(0)
      return
    }
    if (e.key === 'End') {
      e.preventDefault()
      setActive(rows.length - 1)
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      const row = rows[active]
      if (row) run(row)
    }
  }

  return (
    <Modal
      open={open}
      onClose={closeSearch}
      closeAfterTransition
      aria-label="Search and Ask Vera"
      // A scrim must be BLACK in both modes. `theme.palette.black['52%']` looks like
      // the right token and is not: the dark theme deliberately flips the black-alpha
      // ramp to WHITE-alpha (theme.ts darkBlack), which is correct for text, icons and
      // dividers but turns a scrim into a 52% white haze over the page — the grey wash
      // this replaces. Hardcoded rather than tokenised because there is no
      // "always black" alpha in the palette, and inventing one is a design-system
      // change that belongs in @repo/ui, not here.
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
        },
      }}
    >
      <Fade in={open}>
        {/*
          VRG-227 — split in two: an UNZOOMED positioning shell (this Box — plain
          static CSS, `top: 12vh`/`left: 50%`/`translateX(-50%)`, no anchorEl math)
          wrapping a ZOOMED content box (below) that carries every bit of visual
          chrome. `useTextZoom` no longer applies `zoom` to `document.body` —
          `#app` carries it instead — so this Box (portalled into `document.body`
          by the parent `Modal`) is no longer ambiently zoomed the way it used to
          be; it needs its own explicit re-zoom. But it CANNOT go on this element:
          `top`/`left` here are real, viewport-relative values (`12vh`/`50%`),
          and zoom rescales an element's own declared offsets exactly like it
          rescales a Popover's `style.top` — proven in `useTextZoom.ts`'s
          docblock. Putting zoom on the CHILD instead (a plain content box with no
          position of its own) means it just renders bigger, which is the
          correct "scales like a browser zoom" outcome for a centered modal:
          MUI's own Popover/Menu/Tooltip/Drawer/Dialog theme overrides
          (`packages/ui/theme/theme.ts`) use the identical split.
        */}
        <Box onKeyDown={onKeyDown} sx={{ position: 'absolute', top: '12vh', left: '50%', transform: 'translateX(-50%)', outline: 'none' }}>
        <Box
          sx={{
            zoom: 'var(--text-zoom, 1)',
            // The `100vw`/`100vh` terms are pre-divided by the zoom factor, matching
            // this codebase's established `vhMinus`/`--text-zoom` convention
            // (`utils/textZoom.ts`, `Layout.tsx`) — a raw viewport unit measured
            // INSIDE a zoomed element paints z× its real size, so every other
            // viewport-relative box in the app compensates the same way. Without
            // it, this cap would grow past the real viewport at zoom > 1 (visible
            // horizontal/vertical overflow) and under-fill it at zoom < 1.
            width: 'min(640px, calc((100vw - 32px) / var(--text-zoom, 1)))',
            maxHeight: 'min(560px, calc((100vh - 24vh) / var(--text-zoom, 1)))',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            boxShadow: theme.shadows[24],
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 3, pb: 2, flexShrink: 0 }}>
            <Input
              fullWidth
              value={query}
              onChange={setQuery}
              placeholder="Search help, docs, glossary or ask Vera about your data"
              startAdornment={<SearchIcon strokeWidth={1} size={20} />}
              // Input exposes no `autoFocus` prop and its forwarded ref lands on the
              // wrapper div, not the <input> — inputProps is the only route to the field.
              inputProps={{
                autoFocus: true,
                'aria-label': 'Search',
                'aria-autocomplete': 'list',
                'aria-controls': 'search-overlay-results',
                'aria-activedescendant': rows[active]?.id,
              }}
            />
          </Box>

          <Box
            ref={listRef}
            id="search-overlay-results"
            role="listbox"
            sx={{ overflowY: 'auto', flex: 1, minHeight: 0, px: 2, pb: 2 }}
          >
            {rows.length === 0 ? (
              <Stack alignItems="center" spacing={2} sx={{ py: 10, px: 4 }}>
                <Typography variant="body3" color="text.secondary" textAlign="center">
                  Nothing matches “{query.trim()}”.
                </Typography>
                <Typography variant="body4" color="text.secondary" textAlign="center">
                  Try a shorter term, or press Enter on Ask Vera to ask in your own words.
                </Typography>
              </Stack>
            ) : (
              rows.map((row, index) => {
                // Untyped == the tours shown are this page's (see the rows memo), so
                // the heading names that. Both calls take the same flag, or a group
                // boundary would be detected where none exists.
                const toursAreOnPage = query.trim().length === 0
                const group = rowGroup(row, toursAreOnPage)
                const showHeader =
                  index === 0 || rowGroup(rows[index - 1], toursAreOnPage) !== group
                return (
                  <React.Fragment key={row.id}>
                    {showHeader && (
                      <Typography
                        variant="body4"
                        color="text.secondary"
                        sx={{ px: 3, pt: index === 0 ? 1 : 4, pb: 1, display: 'block', fontWeight: 600 }}
                      >
                        {group}
                      </Typography>
                    )}
                    <SearchRow
                      row={row}
                      index={index}
                      selected={index === active}
                      onHover={() => setActive(index)}
                      onSelect={() => run(row)}
                    />
                  </React.Fragment>
                )
              })
            )}
          </Box>

          <Stack
            direction="row"
            spacing={4}
            sx={{
              flexShrink: 0,
              px: 4,
              py: 2,
              borderTop: `1px solid ${theme.palette.divider}`,
              color: 'text.secondary',
            }}
          >
            {/*
              Actions left as TERTIARY buttons, keyboard hints right — the arrangement Vergence's
              palette uses. The bar was three plain text hints, which stated how to drive the list
              but offered no way OUT of it: a search that found nothing left you with no next step.
              These two are that next step.
            */}
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="text"
                disableElevation
                startIcon={<AskVeraIcon size={16} />}
                onClick={() => {
                  ask(query.trim())
                  closeSearch()
                }}
                sx={FOOTER_BUTTON_SX}
              >
                Ask Vera
              </Button>
              <Button
                variant="text"
                disableElevation
                startIcon={<BookOpenIcon strokeWidth={1} size={16} />}
                onClick={() => {
                  navigate(ROUTES.DOCS)
                  closeSearch()
                }}
                sx={FOOTER_BUTTON_SX}
              >
                See docs
              </Button>
              {/* The third way out, alongside "ask a person a question" and "read the
                  manual": be shown. Vergence's palette carries the same trio. */}
              <Button
                variant="text"
                disableElevation
                startIcon={<CompassIcon strokeWidth={1} size={16} />}
                onClick={() => {
                  closeSearch()
                  openToursDialog()
                }}
                sx={FOOTER_BUTTON_SX}
              >
                Product tours
              </Button>
            </Stack>
            <Box sx={{ flex: 1 }} />
            <Hint keys="↑ ↓" label="navigate" />
            <Hint keys="↵" label="open" />
            <Hint keys="esc" label="close" />
          </Stack>
        </Box>
        </Box>
      </Fade>
    </Modal>
  )
}

const Hint = ({ keys, label }: { keys: string; label: string }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <Typography variant="body4" sx={{ fontFamily: 'monospace' }}>
      {keys}
    </Typography>
    <Typography variant="body4">{label}</Typography>
  </Stack>
)

const SearchRow = ({
  row,
  index,
  selected,
  onHover,
  onSelect,
}: {
  row: Row
  index: number
  selected: boolean
  onHover: () => void
  onSelect: () => void
}) => {
  const theme = useTheme()

  let icon: React.ReactNode
  let primary: string
  let secondary: string | undefined
  /**
   * Let the secondary line WRAP instead of truncating it.
   *
   * Every other row's secondary is a path or a title — a label, where an ellipsis costs nothing. A
   * glossary row's secondary is the DEFINITION, which is the answer the reader came for; `noWrap`
   * would cut it mid-sentence and the row would state a term without saying what it means, which is
   * worse than not showing the row. Clamped to two lines so a result list stays scannable.
   */
  let wrapSecondary = false
  /**
   * A short marker rendered beside the label. Today only "Internal", on staff-only doc results.
   *
   * It duplicates the group heading on purpose. The heading is the primary signal, but it is
   * painted once at the top of a block and scrolls out of the list; the badge travels with the
   * row, so a result cannot be read — or clicked, or its title repeated to someone — without the
   * audience attached. The cost of the redundancy is one small label; the cost of the row being
   * ambiguous is an internal runbook quoted to a brand.
   */
  let badge: string | undefined

  if (row.kind === 'ask') {
    icon = <AskVeraIcon size={18} />
    // No query typed yet (the pinned empty-state row, VRG-227): a generic invite to
    // open the panel, rather than an empty pair of quotes, which would read as broken.
    //
    // The label is just "Ask Vera" — the old "Ask Vera about your data" put the SCOPE
    // in the label and then contradicted it in the secondary, which is wider. The row
    // names the destination; the secondary says what you can ask it.
    //
    // ⚠️ TODO(VRG-444 / VRG-445): "the market" is NOT BUILT. This mirrors the Ask
    // composer's placeholder, which names market and benchmark data on the user's
    // explicit instruction because they intend to land both in the same session — see
    // `AskPanel/AskComposer.tsx`, which carries the same warning. Keep the two in sync,
    // and note this is now the SECOND surface advertising the capability: neither may
    // reach `main` ahead of VRG-444/445, or the product promises something a user
    // cannot tell apart from broken (§30).
    primary = row.query ? `Ask Vera about “${row.query}”` : 'Ask Vera'
    secondary = row.query
      ? 'Opens the Ask panel with your question'
      : 'Opens the panel to ask anything about Vera, your data, the market and more'
  } else if (row.kind === 'tour') {
    // MapPin, matching the chip's glyph — the same idea in two surfaces should not
    // wear two glyphs.
    icon = <MapPinIcon strokeWidth={1} size={18} />
    primary = row.tour.label
    /*
      Progress as TEXT, prefixed onto the description — deliberately not the
      library's progress BAR.

      A palette row is one line plus a secondary; every other kind here (commands,
      docs, glossary) carries no graphic, and a bar would give tour rows a visual
      weight nothing else has, in a surface whose job is routing rather than
      browsing. But showing NOTHING made the palette the only place that hid the
      state, so it reads "4 of 6 · <description>", matching the chip's
      "Finish tour · 4 of 6" register. The bar stays in the library, which is a
      browsing surface with room for it.
    */
    secondary = row.progressLabel
      ? `${row.progressLabel} · ${row.tour.description}`
      : row.tour.description
  } else if (row.kind === 'tours-all') {
    icon = <CompassIcon strokeWidth={1} size={18} />
    primary = 'Browse all tours'
    secondary = 'Every walkthrough in the library'
  } else if (row.kind === 'command') {
    /*
      The document glyph belongs to the "Pages" group ONLY.

      "Go to" became "Pages" (an action renamed to the thing the rows are), and a document reads as
      "a page in the product". This is the same `FileTextIcon` the Docs RESULTS use: the two are
      always drawn under their own group headers, so one glyph for "a page you can open" is a
      consistent vocabulary rather than a collision. (An earlier pass used `AppWindowIcon` here to
      keep them apart; a window read as generic chrome across Analytics / Certificates / Products.)

      But commands are not all pages: the same list carries Help ("Docs") and Settings rows, and
      applying the document to every command would give a settings row a page icon. Those keep the
      arrow, which is what they always were — a jump to somewhere.
    */
    icon =
      row.command.group === 'Pages' ? (
        <FileTextIcon strokeWidth={1} size={18} />
      ) : (
        <ArrowRightIcon strokeWidth={1} size={18} />
      )
    primary = row.command.label
    secondary = row.command.path
  } else if (row.kind === 'glossary') {
    const { entry } = row.match
    icon = <BookOpenIcon strokeWidth={1} size={18} />
    primary = entry.term
    secondary = entry.short
    wrapSecondary = true
  } else {
    const { doc, heading } = row.match
    // A section result shows the heading as the headline and the guide as the context,
    // because the heading is the specific thing that matched.
    icon = heading ? <HashIcon strokeWidth={1} size={18} /> : <FileTextIcon strokeWidth={1} size={18} />
    primary = heading ? heading.text : doc.title
    secondary = heading ? doc.title : doc.excerpt
    // Only staff ever see a row with this set — a brand user's list has no internal rows in it
    // to mark. So it reads as "this one is different", not as chrome on every result.
    badge = row.internal ? 'Internal' : undefined
  }

  return (
    <Stack
      direction="row"
      spacing={3}
      alignItems="center"
      id={row.id}
      role="option"
      aria-selected={selected}
      data-row-index={index}
      onMouseMove={onHover}
      onClick={onSelect}
      sx={{
        px: 3,
        py: 2,
        borderRadius: 2,
        cursor: 'pointer',
        // An action row: it activates on click and holds no persistent selected state, so
        // the highlight signals "this is what Enter will do" (working-conventions §34).
        bgcolor: selected ? theme.palette.brand['600-6%'] : 'transparent',
        color: selected ? 'text.primary' : 'text.secondary',
      }}
    >
      <Box sx={{ display: 'flex', flexShrink: 0, color: selected ? theme.palette.brand[600] : 'inherit' }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        {/*
          Weight is FIXED at 400. It used to be `selected ? 600 : 400`, and since the
          list sets `selected` on mouse-move, every row visibly thickened under the
          cursor — the reported "text bolds on hover", found here after I had twice
          looked for it in CSS. Worth recording how it hid: grepping `:hover` blocks
          for `fontWeight` cannot see a weight expressed as a JS ternary on a prop, so
          "no rule changes weight on hover" was true of the stylesheets and false of
          the component.

          Selection is already carried three ways without touching the glyphs — a
          brand-tinted row background, the label going from text.secondary to
          text.primary, and the icon turning brand. A weight change on top of that
          bought nothing and reflowed the line, which in a list you are arrowing
          through means the text shifts as the highlight passes over it.
        */}
        {/*
          The label DIMS when the row is not the active one, so the highlighted row is
          the only white text in the list. It used to be `color="text.primary"`
          unconditionally, which silently overrode the row container's own
          `selected ? primary : secondary` — so every label rendered at full white and
          the only thing distinguishing the active row was its faint tinted
          background. Removing the weight change (which was doing the work of
          separating them, badly) left that flatness exposed.

          Contrast, not weight, is the right carrier here: it separates the rows
          without moving a single glyph, so nothing reflows as the highlight travels.
        */}
        <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
          <Typography
            variant="body3"
            color={selected ? 'text.primary' : 'text.secondary'}
            noWrap
            sx={{ fontWeight: 400, transition: 'color 0.12s ease' }}
          >
            {primary}
          </Typography>
          {badge && (
            /*
              An outlined label rather than a filled chip. The palette is a neutral surface whose
              only colour is the brand tint on the selected row, and a filled badge would compete
              with that — the loudest thing in the list would be a provenance marker rather than
              where the cursor is. `flexShrink: 0` so a long guide title truncates before the
              marker does; a half-visible "Inter…" would be worse than useless.
            */
            <Box
              sx={{
                flexShrink: 0,
                px: 1.5,
                py: 0.25,
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
                fontSize: 10,
                lineHeight: 1.6,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'text.secondary',
                whiteSpace: 'nowrap',
              }}
            >
              {badge}
            </Box>
          )}
        </Stack>
        {secondary && (
          <Typography
            variant="body4"
            color="text.secondary"
            noWrap={!wrapSecondary}
            sx={{
              display: wrapSecondary ? '-webkit-box' : 'block',
              ...(wrapSecondary && {
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: 1.5,
              }),
            }}
          >
            {secondary}
          </Typography>
        )}
      </Box>
      {selected && (
        <Box sx={{ display: 'flex', flexShrink: 0, color: 'text.secondary' }}>
          <CornerDownLeftIcon strokeWidth={1} size={16} />
        </Box>
      )}
    </Stack>
  )
}
